import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Crown, Edit3, Trash2, Loader2, Lock, ArrowRightLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { notesApi } from '../../services/cloudApi';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

/**
 * Ortak not kartındaki avatar alanına tıklanınca açılır; tüm ortak çalışanlar ve rolleri listelenir.
 * Not sahibi (owner): editörleri dondurabilir, kaldırabilir, sahipliği devredebilir.
 */
const CollaboratorsModal = ({ isOpen, onClose, note }) => {
  const { t } = useTranslation('notes');
  const { authToken, user } = useAuth();
  const [removingUserId, setRemovingUserId] = useState(null);
  const [freezingUserId, setFreezingUserId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'remove'|'freeze'|'transfer', userId, name }

  const collaborators = note?.collaborators || [];
  const myRole = collaborators.find(c => c.user_id === user?.id)?.role;
  const isOwner = myRole === 'owner';

  const handleRemove = async (collaboratorUserId) => {
    if (!authToken || !note?.id) return;
    setRemovingUserId(collaboratorUserId);
    setConfirmAction(null);
    try {
      await notesApi.removeCollaborator({ token: authToken, noteId: note.id, userId: collaboratorUserId });
      window.dispatchEvent(new CustomEvent('notesplus-reload-notes'));
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: { type: 'success', title: t('collaboratorsModal.removeSuccessTitle'), message: t('collaboratorsModal.removeSuccessMessage') },
      }));
      onClose();
    } catch (err) {
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: { type: 'error', title: t('common:errorTitle'), message: getApiErrorMessage(err, { fallback: t('collaboratorsModal.removeErrorFallback') }) },
      }));
    } finally {
      setRemovingUserId(null);
    }
  };

  const handleFreeze = async (collaboratorUserId) => {
    if (!authToken || !note?.id) return;
    setFreezingUserId(collaboratorUserId);
    setConfirmAction(null);
    try {
      await notesApi.freezeCollaborator({ token: authToken, noteId: note.id, userId: collaboratorUserId });
      window.dispatchEvent(new CustomEvent('notesplus-reload-notes'));
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: { type: 'success', title: t('collaboratorsModal.freezeSuccessTitle'), message: t('collaboratorsModal.freezeSuccessMessage') },
      }));
      onClose();
    } catch (err) {
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: { type: 'error', title: t('common:errorTitle'), message: getApiErrorMessage(err, { fallback: t('collaboratorsModal.freezeErrorFallback') }) },
      }));
    } finally {
      setFreezingUserId(null);
    }
  };

  const handleTransferOwnership = async (targetUserId) => {
    if (!authToken || !note?.id) return;
    setConfirmAction(null);
    try {
      await notesApi.transferNoteOwnership({ token: authToken, noteId: note.id, newOwnerUserId: targetUserId });
      window.dispatchEvent(new CustomEvent('notesplus-reload-notes'));
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: { type: 'success', title: t('transferOwnership.successTitle'), message: t('collaboratorsModal.transferSuccessMessage') },
      }));
      onClose();
    } catch (err) {
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: { type: 'error', title: t('common:errorTitle'), message: getApiErrorMessage(err, { fallback: t('transferOwnership.errorFallback') }) },
      }));
    }
  };

  const requestConfirm = (type, userId, name) => {
    setConfirmAction({ type, userId, name });
  };

  const cancelConfirm = () => {
    setConfirmAction(null);
  };

  const executeConfirm = () => {
    if (!confirmAction) return;
    const { type, userId } = confirmAction;
    if (type === 'remove') handleRemove(userId);
    else if (type === 'freeze') handleFreeze(userId);
    else if (type === 'transfer') handleTransferOwnership(userId);
  };

  const getActionLabel = (type) => {
    if (type === 'remove') return t('collaboratorsModal.actionLabels.remove');
    if (type === 'freeze') return t('collaboratorsModal.actionLabels.freeze');
    if (type === 'transfer') return t('collaboratorsModal.actionLabels.transfer');
    return '';
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-[100] bg-slate-900/30 backdrop-blur-sm flex items-center justify-center"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="fixed inset-0 flex items-center justify-center p-4 z-[10001]"
        onClick={onClose}
      >
        <div
          className="bg-[var(--color-bg-base)] rounded-2xl shadow-2xl w-full max-w-sm border border-white/[0.07] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/[0.07]">
            <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">
              {t('collaboratorsModal.title')}
            </h3>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)] transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* List */}
          <div className="p-3 max-h-80 overflow-y-auto space-y-1">
            {collaborators.length === 0 ? (
              <p className="text-sm text-[var(--color-text-muted)] text-center py-4">{t('collaboratorsModal.noCollaborators')}</p>
            ) : (
              collaborators.map((c) => {
                const name = c.name || c.email || t('transferOwnership.unknownUser');
                const isCollabOwner = c.role === 'owner';
                const isMe = c.user_id === user?.id;
                const isRemoving = removingUserId === c.user_id;
                const isFreezing = freezingUserId === c.user_id;
                const isPendingConfirm = confirmAction?.userId === c.user_id;

                return (
                  <div
                    key={c.user_id}
                    className="rounded-xl bg-[var(--color-bg-muted)] border border-white/[0.05] overflow-hidden"
                  >
                    {/* Collaborator row */}
                    <div className="flex items-center gap-3 p-3">
                      {/* Avatar */}
                      <div className="w-9 h-9 rounded-full bg-[#161f31] flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {c.avatar_url ? (
                          <img src={c.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <span className="text-xs font-bold text-[var(--color-text-secondary)]">
                            {(c.name || c.email || '?').slice(0, 2).toUpperCase()}
                          </span>
                        )}
                      </div>

                      {/* Name + role */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[var(--color-text-primary)] truncate">
                          {name}{isMe && <span className="text-xs text-[var(--color-text-muted)] ml-1">{t('collaboratorsModal.meLabel')}</span>}
                        </p>
                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                          {isCollabOwner ? (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-400">
                              <Crown className="w-3 h-3" />
                              {t('collaboratorsModal.ownerBadge')}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-[var(--color-text-muted)]">
                              <Edit3 className="w-3 h-3" />
                              {t('collaboratorsModal.editorBadge')}
                            </span>
                          )}
                          {c.is_frozen && (
                            <span className="inline-flex items-center gap-1 text-xs text-orange-400 bg-orange-400/10 px-1.5 py-0.5 rounded">
                              <Lock className="w-2.5 h-2.5" />
                              {t('collaboratorsModal.readOnlyBadge')}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Action buttons */}
                      {isOwner && !isCollabOwner && !isMe && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {/* Freeze button — only if not already frozen */}
                          {!c.is_frozen && (
                            <button
                              onClick={() => requestConfirm('freeze', c.user_id, name)}
                              disabled={isFreezing || isRemoving}
                              className="p-1.5 text-orange-400 hover:text-orange-300 hover:bg-orange-400/10 rounded-lg transition-colors"
                              title={t('collaboratorsModal.freezeTooltip')}
                            >
                              {isFreezing ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Lock className="w-3.5 h-3.5" />
                              )}
                            </button>
                          )}
                          {/* Transfer button */}
                          <button
                            onClick={() => requestConfirm('transfer', c.user_id, name)}
                            disabled={isFreezing || isRemoving}
                            className="p-1.5 text-[#3d8ef0] hover:text-[#5ca3f5] hover:bg-[#2563eb]/10 rounded-lg transition-colors"
                            title={t('collaboratorsModal.transferTooltip')}
                          >
                            <ArrowRightLeft className="w-3.5 h-3.5" />
                          </button>
                          {/* Remove button */}
                          <button
                            onClick={() => requestConfirm('remove', c.user_id, name)}
                            disabled={isRemoving || isFreezing}
                            className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-lg transition-colors"
                            title={t('collaboratorsModal.removeTooltip')}
                          >
                            {isRemoving ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Inline confirmation bar */}
                    {isPendingConfirm && (
                      <div className="flex items-center justify-between px-3 py-2 bg-[var(--color-bg-elevated)] border-t border-white/[0.06]">
                        <span className="text-xs text-[var(--color-text-secondary)]">
                          {t('collaboratorsModal.confirmQuestion', { name: confirmAction.name, action: getActionLabel(confirmAction.type) })}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={cancelConfirm}
                            className="px-2 py-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] hover:bg-white/5 rounded transition-colors"
                          >
                            {t('common:cancel')}
                          </button>
                          <button
                            onClick={executeConfirm}
                            className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                              confirmAction.type === 'remove'
                                ? 'bg-red-500/20 text-red-300 hover:bg-red-500/30'
                                : confirmAction.type === 'freeze'
                                ? 'bg-orange-500/20 text-orange-300 hover:bg-orange-500/30'
                                : 'bg-[#2563eb]/20 text-[#7ab8f8] hover:bg-[#2563eb]/30'
                            }`}
                          >
                            {t('common:confirm')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer hint */}
          {isOwner && collaborators.length > 1 && (
            <div className="px-4 pb-4 pt-1">
              <p className="text-xs text-[var(--color-text-muted)]">
                {t('collaboratorsModal.footerHint')}
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CollaboratorsModal;
